import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TopBar from './TopBar';
import {
  LayoutDashboard,
  ShoppingCart,
  ChefHat,
  ClipboardList,
  Armchair,
  CalendarDays,
  BookOpen,
  Package,
  Users,
  Badge,
  TrendingUp,
  Sparkles,
  Bike,
  Wallet,
  Warehouse,
  Receipt,
  CalendarCheck,
  QrCode,
  UserCog,
  Building2,
  LogOut,
  X,
  ChevronsLeft,
  ChevronsRight,
  Settings,
} from 'lucide-react';

const SECTIONS = [
  {
    title: 'Operations',
    items: [
      { path: '/',             label: 'Dashboard',     icon: LayoutDashboard, roles: ['super_admin', 'admin', 'manager', 'chef', 'cashier', 'waiter', 'rider', 'staff'] },
      // POS is the cashier's station (payments live there) — waiters punch
      // orders from the Orders page instead.
      { path: '/pos',          label: 'POS',           icon: ShoppingCart,    roles: ['super_admin', 'admin', 'manager', 'cashier'] },
      { path: '/kds',          label: 'KDS',           icon: ChefHat,         roles: ['super_admin', 'admin', 'manager', 'chef'] },
      { path: '/orders',       label: 'Orders',        icon: ClipboardList,   roles: ['super_admin', 'admin', 'manager', 'cashier', 'waiter'] },
      { path: '/tables',       label: 'Tables',        icon: Armchair,        roles: ['super_admin', 'admin', 'manager', 'cashier', 'waiter'] },
      { path: '/reservations', label: 'Reservations',  icon: CalendarDays,    roles: ['super_admin', 'admin', 'manager', 'cashier', 'waiter'] },
    ],
  },
  {
    title: 'Inventory',
    items: [
      { path: '/menu',         label: 'Menu',          icon: BookOpen,        roles: ['super_admin', 'admin', 'manager'] },
      { path: '/inventory',    label: 'Inventory',     icon: Package,         roles: ['super_admin', 'admin', 'manager'] },
      { path: '/warehouse',    label: 'Warehouse',     icon: Warehouse,       roles: ['super_admin', 'admin', 'manager'] },
    ],
  },
  {
    title: 'People',
    items: [
      { path: '/customers',    label: 'Customers',     icon: Users,           roles: ['super_admin', 'admin', 'manager', 'cashier', 'waiter'] },
      { path: '/staff',        label: 'Staff',         icon: Badge,           roles: ['super_admin', 'admin', 'manager', 'staff', 'cashier', 'waiter'] },
      { path: '/payroll',      label: 'Payroll',       icon: Wallet,          roles: ['super_admin', 'admin', 'manager'] },
      { path: '/riders',       label: 'Riders',        icon: Bike,            roles: ['super_admin', 'admin', 'manager', 'rider'] },
    ],
  },
  {
    title: 'Insights',
    items: [
      { path: '/analytics',    label: 'Analytics',     icon: TrendingUp,      roles: ['super_admin', 'admin', 'manager'] },
      { path: '/ai',           label: 'AI Insights',   icon: Sparkles,        roles: ['super_admin', 'admin', 'manager'] },
    ],
  },
  {
    title: 'Admin',
    items: [
      { path: '/expenses',     label: 'Expenses',      icon: Receipt,         roles: ['super_admin', 'admin', 'manager'] },
      { path: '/closing',      label: 'Day Close',     icon: CalendarCheck,   roles: ['super_admin', 'admin', 'manager'] },
      { path: '/qrcode',       label: 'QR Code',       icon: QrCode,          roles: ['super_admin', 'admin', 'manager'] },
      { path: '/users',        label: 'Users',         icon: UserCog,         roles: ['super_admin', 'admin'] },
      { path: '/outlets',      label: 'Outlets',       icon: Building2,       roles: ['super_admin'] },
      { path: '/settings',     label: 'Settings',      icon: Settings,        roles: ['super_admin', 'admin'] },
    ],
  },
];

const initialsOf = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

// A small dot on the avatar conveys role instead of a second text label next
// to the name — some accounts have a name that reads the same as their role
// (e.g. a "Super Admin" user with the super_admin role), which made the old
// text+badge layout look like it was showing the same thing twice.
const roleDotColor = (role) => {
  switch (role) {
    case 'super_admin': return 'bg-danger-500';
    case 'admin':       return 'bg-primary-500';
    case 'manager':     return 'bg-warning-500';
    case 'chef':        return 'bg-success-500';
    default:            return 'bg-neutral-400';
  }
};

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Desktop-only icon-rail collapse; remembered across sessions. Mobile
  // always renders the full drawer regardless of this — collapsing only
  // ever applies at the md breakpoint and up.
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');

  // Close the mobile drawer automatically whenever the route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(collapsed));
  }, [collapsed]);

  return (
    <div className="flex h-screen bg-neutral-50 text-neutral-900 overflow-hidden">
      {/* Mobile backdrop — click to close the drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — collapsible icon-rail on desktop, off-canvas drawer on mobile */}
      <aside
        className={`
          fixed md:static inset-y-0 left-0 z-50
          w-72 ${collapsed ? 'md:w-[76px]' : 'md:w-64'} h-full max-h-screen
          bg-neutral-900 text-neutral-100 flex flex-col flex-shrink-0
          transform transition-transform duration-200 ease-in-out md:transition-[width]
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
        `}
      >
        {/* Brand */}
        <div className={`px-5 py-4 border-b border-neutral-800 flex items-center gap-3 flex-shrink-0 ${collapsed ? 'md:justify-center md:px-2' : ''}`}>
          <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center p-1 shadow-sm ring-1 ring-white/10 flex-shrink-0">
            <img src="/chenab-logo.jpeg" alt="Chenab System" className="w-full h-full object-contain" />
          </div>
          <div className={`leading-tight min-w-0 ${collapsed ? 'md:hidden' : ''}`}>
            <p className="text-sm font-semibold tracking-tight truncate">Chenab System</p>
            <p className="text-[11px] text-neutral-400 truncate">Management Suite</p>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="ml-auto md:hidden text-neutral-400 hover:text-white p-1 -mr-1"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* Nav — the only part that scrolls; scrollbar hidden but fully functional */}
        <nav className="flex-1 min-h-0 overflow-y-auto scrollbar-hide py-3 px-2">
          {SECTIONS.map((section) => {
            const visibleItems = section.items.filter((it) => it.roles.includes(user?.role));
            if (visibleItems.length === 0) return null;
            return (
              <div key={section.title} className="mb-4">
                <p className={`px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-500 ${collapsed ? 'md:hidden' : ''}`}>
                  {section.title}
                </p>
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  const active = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className={`
                        group relative flex items-center gap-3 px-3 py-2 rounded-md text-sm
                        transition-colors mb-0.5
                        ${collapsed ? 'md:justify-center md:px-2' : ''}
                        ${active
                          ? 'bg-primary-600/20 text-white border-l-2 border-primary-500 pl-[10px]'
                          : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'}
                      `}
                    >
                      <Icon size={18} className={`flex-shrink-0 ${active ? 'text-primary-300' : 'text-neutral-400 group-hover:text-neutral-200'}`} />
                      <span className={collapsed ? 'md:hidden' : ''}>{item.label}</span>
                      {collapsed && (
                        <span className="hidden md:group-hover:block absolute left-full ml-2 whitespace-nowrap rounded-md bg-neutral-800 px-2 py-1 text-xs text-white shadow-lg z-50">
                          {item.label}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* User row + logout — compact, fixed at the bottom. Role shown as a
            small dot on the avatar (not a second text line) and the collapse
            toggle sits right in front of the name. */}
        <div className="border-t border-neutral-800 p-2 flex-shrink-0">
          <div className={`flex items-center gap-2 px-1 py-1.5 rounded-md ${collapsed ? 'md:justify-center' : ''}`}>
            <button
              onClick={() => setCollapsed((c) => !c)}
              className="hidden md:flex w-6 h-6 rounded-md items-center justify-center text-neutral-400 hover:text-white hover:bg-neutral-800 transition-colors flex-shrink-0"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
            </button>

            <div className="relative group flex-shrink-0">
              <div className="w-8 h-8 bg-neutral-700 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                {initialsOf(user?.name)}
              </div>
              <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-neutral-900 ${roleDotColor(user?.role)}`} />
              {collapsed && (
                <span className="hidden md:group-hover:block absolute left-full ml-2 bottom-0 whitespace-nowrap rounded-md bg-neutral-800 px-2 py-1 text-xs text-white shadow-lg z-50">
                  {user?.name || 'User'} · <span className="capitalize">{user?.role?.replace('_', ' ')}</span>
                </span>
              )}
            </div>

            <p className={`text-sm font-medium text-white truncate flex-1 min-w-0 ${collapsed ? 'md:hidden' : ''}`}>
              {user?.name || 'User'}
            </p>
          </div>
          <button
            onClick={logout}
            className="mt-1.5 w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-md bg-neutral-800 hover:bg-danger-600/80 text-neutral-200 hover:text-white text-sm font-medium transition-colors"
            title="Sign out"
          >
            <LogOut size={15} />
            <span className={collapsed ? 'md:hidden' : ''}>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0 w-full overflow-hidden">
        <TopBar onMenuClick={() => setMobileOpen(true)} />
        <div className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
